import { useAuthStore } from '@/stores/auth';

// VITE_API_URL should already include /api/v1, e.g. http://localhost:8787/api/v1
// If not set, we use /api/v1 as the path (same-origin requests proxied by Vite)
const API_BASE_URL = import.meta.env.VITE_API_URL || '/api/v1';

interface ApiError {
    error: {
        code: string;
        message: string;
        details?: Record<string, string>[];
    };
}

class ApiClient {
    private async request<T>(
        endpoint: string,
        options: RequestInit = {}
    ): Promise<T> {
        const token = useAuthStore.getState().token;

        const headers: HeadersInit = {
            'Content-Type': 'application/json',
            ...(token && { Authorization: `Bearer ${token}` }),
            ...options.headers,
        };

        const response = await fetch(`${API_BASE_URL}${endpoint}`, {
            ...options,
            headers,
            credentials: 'include',
        });

        const data = await response.json();

        if (!response.ok) {
            const error = data as ApiError;
            throw new Error(error.error?.message || 'Request failed');
        }

        return data;
    }

    // Auth endpoints
    async register(email: string, password: string, fullName: string, referralCode?: string) {
        return this.request<{
            success: boolean;
            message: string;
            email: string;
            requires_verification: boolean;
        }>('/auth/register', {
            method: 'POST',
            body: JSON.stringify({
                email,
                password,
                full_name: fullName,
                ...(referralCode && { referral_code: referralCode })
            }),
        });
    }

    async login(email: string, password: string) {
        return this.request<{
            user?: { id: string; email: string; full_name: string; avatar_url: string | null };
            workspace?: { id: string; name: string; slug: string } | null;
            access_token?: string;
            // 2FA fields
            requires_2fa?: boolean;
            temp_token?: string;
            email?: string;
        }>('/auth/login', {
            method: 'POST',
            body: JSON.stringify({ email, password }),
        });
    }

    async loginWithGoogle(credential: string, referralCode?: string) {
        return this.request<{
            user: { id: string; email: string; full_name: string; avatar_url: string | null };
            workspace: { id: string; name: string; slug: string } | null;
            access_token: string;
            is_new_user?: boolean;
        }>('/auth/google', {
            method: 'POST',
            body: JSON.stringify({
                credential,
                ...(referralCode && { referral_code: referralCode })
            }),
        });
    }

    async logout() {
        return this.request('/auth/logout', { method: 'POST' });
    }

    // DEV ONLY: Demo login for localhost testing
    async demoLogin() {
        return this.request<{
            user: { id: string; email: string; full_name: string; avatar_url: string | null };
            workspace: { id: string; name: string; slug: string } | null;
            access_token: string;
            demo_credentials: { email: string; password: string };
        }>('/auth/demo-login', {
            method: 'POST',
        });
    }

    async verifyEmail(token: string) {
        return this.request<{
            success: boolean;
            message: string;
            user: { id: string; email: string; full_name: string };
            workspace: { id: string; name: string; slug: string } | null;
            access_token: string;
            expires_in: number;
        }>('/auth/verify-email', {
            method: 'POST',
            body: JSON.stringify({ token }),
        });
    }

    async resendVerification(email: string) {
        return this.request<{
            success: boolean;
            message: string;
        }>('/auth/resend-verification', {
            method: 'POST',
            body: JSON.stringify({ email }),
        });
    }

    async changePassword(currentPassword: string, newPassword: string) {
        return this.request<{ success: boolean; message: string }>('/auth/change-password', {
            method: 'POST',
            body: JSON.stringify({
                current_password: currentPassword,
                new_password: newPassword
            }),
        });
    }

    // 2FA endpoints
    async setup2FA() {
        return this.request<{
            secret: string;
            qr_code_url: string;
            manual_entry_key: string;
        }>('/auth/2fa/setup', {
            method: 'POST',
        });
    }

    async verifySetup2FA(code: string) {
        return this.request<{
            success: boolean;
            message: string;
            recovery_codes: string[];
        }>('/auth/2fa/verify-setup', {
            method: 'POST',
            body: JSON.stringify({ code }),
        });
    }

    async verify2FA(tempToken: string, code: string) {
        return this.request<{
            user: { id: string; email: string; full_name: string; avatar_url: string | null };
            workspace: { id: string; name: string; slug: string } | null;
            access_token: string;
            expires_in: number;
        }>('/auth/2fa/verify', {
            method: 'POST',
            body: JSON.stringify({ temp_token: tempToken, code }),
        });
    }

    async useRecoveryCode(tempToken: string, recoveryCode: string) {
        return this.request<{
            user: { id: string; email: string; full_name: string; avatar_url: string | null };
            workspace: { id: string; name: string; slug: string } | null;
            access_token: string;
            expires_in: number;
            remaining_recovery_codes: number;
        }>('/auth/2fa/recovery', {
            method: 'POST',
            body: JSON.stringify({ temp_token: tempToken, recovery_code: recoveryCode }),
        });
    }

    async disable2FA(code: string, password?: string) {
        return this.request<{ success: boolean; message: string }>('/auth/2fa/disable', {
            method: 'POST',
            body: JSON.stringify({ code, password }),
        });
    }

    async get2FAStatus() {
        return this.request<{
            enabled: boolean;
            enabled_at: string | null;
            recovery_codes_remaining: number;
        }>('/auth/2fa/status', {
            method: 'GET',
        });
    }

    async request2FAReset(email: string) {
        return this.request<{ success: boolean; message: string }>('/auth/2fa/reset-request', {
            method: 'POST',
            body: JSON.stringify({ email }),
        });
    }

    async confirm2FAReset(token: string) {
        return this.request<{ success: boolean; message: string; email: string }>('/auth/2fa/reset-confirm', {
            method: 'POST',
            body: JSON.stringify({ token }),
        });
    }

    // Password reset endpoints
    async requestPasswordReset(email: string) {
        return this.request<{ success: boolean; message: string }>('/auth/forgot-password', {
            method: 'POST',
            body: JSON.stringify({ email }),
        });
    }

    async confirmPasswordReset(token: string, newPassword: string) {
        return this.request<{ success: boolean; message: string; email: string }>('/auth/reset-password', {
            method: 'POST',
            body: JSON.stringify({ token, new_password: newPassword }),
        });
    }

    async getMe() {
        return this.request<{
            user: { id: string; email: string; full_name: string; avatar_url: string | null; plan?: 'free' | 'pro' };
            workspaces: Array<{ id: string; name: string; slug: string; role: string }>;
        }>('/auth/me');
    }

    async deleteAccount(password: string) {
        return this.request<{ success: boolean }>('/auth/delete-account', {
            method: 'POST',
            body: JSON.stringify({ password }),
        });
    }

    // Objectives endpoints
    async getObjectives(workspaceId: string, year?: number, periodType?: string) {
        const params = new URLSearchParams({ workspace_id: workspaceId });
        if (year) params.append('year', year.toString());
        if (periodType) params.append('period_type', periodType);

        return this.request<{
            data: Array<{
                id: string;
                title: string;
                description: string | null;
                period_type: string;
                year: number;
                progress: number;
                status: string;
                key_results: Array<{
                    id: string;
                    title: string;
                    progress: number;
                    status: string;
                    current_value: number;
                    target_value: number;
                    unit: string | null;
                }>;
            }>;
            stats: {
                total: number;
                on_track: number;
                at_risk: number;
                off_track: number;
                average_progress: number;
            };
        }>(`/objectives?${params}`);
    }

    async createObjective(data: {
        workspace_id: string;
        title: string;
        description?: string;
        period_type: string;
        year: number;
        start_date: string;
        end_date: string;
        status?: 'draft' | 'active' | 'completed' | 'cancelled';
    }) {
        return this.request<{ data: any }>('/objectives', {
            method: 'POST',
            body: JSON.stringify(data),
        });
    }

    async createKeyResult(objectiveId: string, data: {
        title: string;
        metric_type: string;
        start_value?: number;
        target_value: number;
        unit?: string;
        start_date?: string;
        end_date?: string;
    }) {
        return this.request<{ data: any }>(`/objectives/${objectiveId}/key-results`, {
            method: 'POST',
            body: JSON.stringify(data),
        });
    }

    async checkInKeyResult(krId: string, data: {
        new_value: number;
        note?: string;
    }) {
        return this.request<{ data: any }>(`/key-results/${krId}/check-in`, {
            method: 'POST',
            body: JSON.stringify(data),
        });
    }

    // Boards endpoints
    async getBoards(workspaceId: string) {
        return this.request<{
            data: Array<{
                id: string;
                name: string;
                description: string | null;
                color: string | null;
                task_count: number;
            }>;
        }>(`/boards?workspace_id=${workspaceId}`);
    }

    async getBoard(boardId: string) {
        return this.request<{
            data: {
                id: string;
                name: string;
                description: string | null;
                columns: Array<{
                    id: string;
                    name: string;
                    position: number;
                    wip_limit: number | null;
                    is_done_column: number;
                    tasks: Array<{
                        id: string;
                        title: string;
                        description: string | null;
                        priority: string;
                        due_date: string | null;
                        position: number;
                        kr_link_count: number;
                        subtask_count: number;
                        subtask_completed_count: number;
                    }>;
                }>;
            };
        }>(`/boards/${boardId}`);
    }

    async createBoard(data: {
        workspace_id: string;
        name: string;
        description?: string;
        template?: string;
        linked_kr_id?: string;
    }) {
        return this.request<{ data: any }>('/boards', {
            method: 'POST',
            body: JSON.stringify(data),
        });
    }

    // Tasks endpoints
    async createTask(data: {
        board_id: string;
        column_id: string;
        title: string;
        description?: string;
        priority?: string;
        due_date?: string;
        key_result_ids?: string[];
    }) {
        return this.request<{ data: any }>('/tasks', {
            method: 'POST',
            body: JSON.stringify(data),
        });
    }

    async moveTask(taskId: string, columnId: string, position: number) {
        return this.request<{ data: any }>(`/tasks/${taskId}/move`, {
            method: 'PATCH',
            body: JSON.stringify({ column_id: columnId, position }),
        });
    }

    async linkTaskToKR(taskId: string, keyResultId: string, weight?: number) {
        return this.request<{ data: any }>(`/tasks/${taskId}/link-kr`, {
            method: 'POST',
            body: JSON.stringify({
                key_result_id: keyResultId,
                contribution_weight: weight || 0.5,
            }),
        });
    }

    async unlinkTaskFromKR(taskId: string, krId: string) {
        return this.request(`/tasks/${taskId}/link-kr/${krId}`, {
            method: 'DELETE',
        });
    }

    async getTask(taskId: string) {
        return this.request<{ data: any }>(`/tasks/${taskId}`);
    }

    async updateTask(taskId: string, data: {
        title?: string;
        description?: string | null;
        priority?: 'low' | 'medium' | 'high' | 'urgent';
        due_date?: string | null;
        assignee_id?: string | null;
        labels?: string[];
    }) {
        return this.request<{ data: any }>(`/tasks/${taskId}`, {
            method: 'PATCH',
            body: JSON.stringify(data),
        });
    }

    async deleteTask(taskId: string) {
        return this.request(`/tasks/${taskId}`, { method: 'DELETE' });
    }

    // Analytics endpoints
    async getAnalyticsOverview(workspaceId: string) {
        return this.request<{
            data: {
                objectives: {
                    total: number;
                    on_track: number;
                    at_risk: number;
                    off_track: number;
                    completed: number;
                    average_progress: number;
                };
                tasks: {
                    total: number;
                    completed: number;
                    pending: number;
                    overdue: number;
                    completion_rate: number;
                };
                alignment: {
                    score: number;
                    linked_tasks: number;
                    total_tasks: number;
                };
                recent_activity: {
                    check_ins: Array<{ date: string; count: number }>;
                    completed_tasks: Array<{ date: string; count: number }>;
                };
            };
        }>(`/analytics/overview?workspace_id=${workspaceId}`);
    }

    async getDriftAnalysis(workspaceId: string) {
        return this.request<{
            data: {
                objectives_at_risk: Array<{
                    objective_id: string;
                    objective_title: string;
                    expected_progress: number;
                    actual_progress: number;
                    drift_amount: number;
                    severity: 'low' | 'medium' | 'high';
                    days_remaining: number;
                    recommendation: string;
                }>;
                unlinked_tasks: Array<{
                    id: string;
                    title: string;
                    priority: string;
                    board_name: string;
                }>;
                summary: {
                    total_objectives: number;
                    high_risk: number;
                    medium_risk: number;
                    on_track: number;
                    unlinked_task_count: number;
                };
            };
        }>(`/analytics/drift?workspace_id=${workspaceId}`);
    }

    async getAlignmentTree(workspaceId: string, year?: number) {
        const params = new URLSearchParams({ workspace_id: workspaceId });
        if (year) params.append('year', year.toString());
        return this.request<{
            data: {
                alignment_tree: Array<{
                    objective: {
                        id: string;
                        title: string;
                        progress: number;
                        status: string;
                        period_type: string;
                    };
                    key_results: Array<{
                        id: string;
                        title: string;
                        progress: number;
                        status: string;
                        linked_tasks: Array<{
                            id: string;
                            title: string;
                            priority: string;
                            completed_at: string | null;
                            board_name: string;
                        }>;
                        task_stats: {
                            total: number;
                            completed: number;
                            progress: number;
                        };
                    }>;
                    stats: {
                        kr_count: number;
                        total_linked_tasks: number;
                        completed_linked_tasks: number;
                        has_unlinked_krs: boolean;
                    };
                }>;
                orphan_tasks: Array<{
                    id: string;
                    title: string;
                    priority: string;
                    due_date: string | null;
                    board_name: string;
                    board_id: string;
                }>;
                summary: {
                    alignment_score: number;
                    linked_tasks: number;
                    total_active_tasks: number;
                    orphan_task_count: number;
                    objectives_count: number;
                    krs_without_tasks: number;
                };
            };
        }>(`/analytics/alignment-tree?${params.toString()}`);
    }

    // Update/Delete Objective
    async updateObjective(objectiveId: string, data: {
        title?: string;
        description?: string;
        period_type?: string;
        year?: number;
        start_date?: string;
        end_date?: string;
        status?: 'draft' | 'active' | 'completed' | 'cancelled';
    }) {
        return this.request<{ data: any }>(`/objectives/${objectiveId}`, {
            method: 'PATCH',
            body: JSON.stringify(data),
        });
    }

    async deleteObjective(objectiveId: string) {
        return this.request(`/objectives/${objectiveId}`, { method: 'DELETE' });
    }

    // Delete Key Result
    async deleteKeyResult(krId: string) {
        return this.request(`/key-results/${krId}`, { method: 'DELETE' });
    }

    // Update Board
    async updateBoard(boardId: string, data: {
        name?: string;
        description?: string;
        color?: string;
        linked_kr_id?: string;
    }) {
        return this.request<{ data: any }>(`/boards/${boardId}`, {
            method: 'PATCH',
            body: JSON.stringify(data),
        });
    }

    async deleteBoard(boardId: string) {
        return this.request(`/boards/${boardId}`, { method: 'DELETE' });
    }

    // Notifications endpoints
    async getNotifications(options?: { limit?: number; unreadOnly?: boolean }) {
        const params = new URLSearchParams();
        if (options?.limit) params.append('limit', options.limit.toString());
        if (options?.unreadOnly) params.append('unread', 'true');

        return this.request<{
            data: Array<{
                id: string;
                type: string;
                title: string;
                message: string;
                data: Record<string, any> | null;
                action_url: string | null;
                is_read: number;
                created_at: string;
            }>;
            unread_count: number;
        }>(`/notifications?${params.toString()}`);
    }

    async markNotificationRead(notificationId: string) {
        return this.request(`/notifications/${notificationId}/read`, { method: 'PATCH' });
    }

    async markAllNotificationsRead() {
        return this.request('/notifications/read-all', { method: 'POST' });
    }

    async deleteNotification(notificationId: string) {
        return this.request(`/notifications/${notificationId}`, { method: 'DELETE' });
    }

    async getNotificationPreferences() {
        return this.request<{
            success: boolean;
            data: {
                email_daily_digest: boolean;
                email_task_due: boolean;
                email_kr_at_risk: boolean;
                push_drift_alert: boolean;
                push_mentions: boolean;
                quiet_hours: boolean;
            };
        }>('/notifications/preferences');
    }

    async updateNotificationPreferences(prefs: {
        email_daily_digest?: boolean;
        email_task_due?: boolean;
        email_kr_at_risk?: boolean;
        push_drift_alert?: boolean;
        push_mentions?: boolean;
        quiet_hours?: boolean;
    }) {
        return this.request<{ success: boolean; message: string }>('/notifications/preferences', {
            method: 'PUT',
            body: JSON.stringify(prefs),
        });
    }

    // ============================================
    // WORKSPACE TEAM MANAGEMENT
    // ============================================

    async getWorkspaces() {
        return this.request<{
            workspaces: Array<{
                id: string;
                name: string;
                slug: string;
                settings: string;
                created_at: string;
                role: string;
                member_count: number;
            }>;
        }>('/workspaces');
    }

    async createWorkspace(name: string) {
        return this.request<{
            success: boolean;
            workspace: {
                id: string;
                name: string;
                slug: string;
                owner_id: string;
                created_at: string;
            };
        }>('/workspaces', {
            method: 'POST',
            body: JSON.stringify({ name }),
        });
    }

    async deleteWorkspace(workspaceId: string) {
        return this.request<{ success: boolean; message: string }>(`/workspaces/${workspaceId}`, {
            method: 'DELETE',
        });
    }

    async getWorkspaceMembers(workspaceId: string) {
        return this.request<{
            members: Array<{
                id: string;
                user_id: string;
                role: 'owner' | 'admin' | 'member' | 'viewer';
                joined_at: string;
                full_name: string;
                email: string;
                avatar_url: string | null;
            }>;
            currentUserRole: string;
            memberLimit: number;
        }>(`/workspaces/${workspaceId}/members`);
    }

    async getWorkspaceInvitations(workspaceId: string) {
        return this.request<{
            invitations: Array<{
                id: string;
                email: string;
                role: string;
                status: string;
                created_at: string;
                expires_at: string;
                invited_by_name: string;
            }>;
        }>(`/workspaces/${workspaceId}/invitations`);
    }

    async inviteMember(workspaceId: string, email: string, role: 'admin' | 'member' | 'viewer' = 'member') {
        return this.request<{
            success: boolean;
            invitation: {
                id: string;
                email: string;
                role: string;
                inviteLink: string;
                expiresAt: string;
            };
            emailSent: boolean;
        }>(`/workspaces/${workspaceId}/members/invite`, {
            method: 'POST',
            body: JSON.stringify({ email, role }),
        });
    }

    async getInvitationDetails(token: string) {
        return this.request<{
            invitation: {
                id: string;
                email: string;
                role: string;
                workspaceId: string;
                workspaceName: string;
                invitedByName: string;
                expiresAt: string;
            };
        }>(`/workspaces/join/${token}`);
    }

    async acceptInvitation(token: string) {
        return this.request<{
            success: boolean;
            message: string;
            workspaceId: string;
            role?: string;
        }>(`/workspaces/join/${token}`, {
            method: 'POST',
        });
    }

    async getMyInvitations() {
        return this.request<{
            invitations: Array<{
                id: string;
                role: string;
                token: string;
                inviteLink: string;
                createdAt: string;
                expiresAt: string;
                workspace: {
                    id: string;
                    name: string;
                };
                invitedBy: {
                    name: string;
                    avatar: string | null;
                };
            }>;
        }>('/workspaces/my-invitations');
    }

    async changeMemberRole(workspaceId: string, memberId: string, role: 'admin' | 'member' | 'viewer') {
        return this.request<{
            success: boolean;
            member: { userId: string; role: string };
        }>(`/workspaces/${workspaceId}/members/${memberId}/role`, {
            method: 'PATCH',
            body: JSON.stringify({ role }),
        });
    }

    async removeMember(workspaceId: string, memberId: string) {
        return this.request<{
            success: boolean;
            message: string;
        }>(`/workspaces/${workspaceId}/members/${memberId}`, {
            method: 'DELETE',
        });
    }

    // ============================================
    // BLOG MANAGEMENT
    // ============================================

    async getBlogPosts(options?: { published?: boolean; featured?: boolean }) {
        const params = new URLSearchParams();
        if (options?.published !== undefined) params.append('published', options.published.toString());
        if (options?.featured !== undefined) params.append('featured', options.featured.toString());

        return this.request<Array<{
            id: string;
            slug: string;
            title: string;
            excerpt: string;
            content: string;
            read_time: string;
            featured: boolean;
            published: boolean;
            tags: string[];
            author_id: string;
            created_at: string;
            updated_at: string;
        }>>(`/blog/posts?${params.toString()}`);
    }

    async getBlogPost(slug: string) {
        return this.request<{
            id: string;
            slug: string;
            title: string;
            excerpt: string;
            content: string;
            read_time: string;
            featured: boolean;
            published: boolean;
            tags: string[];
            author_id: string;
            created_at: string;
            updated_at: string;
        }>(`/blog/posts/${slug}`);
    }

    async getAdminBlogPosts(params?: { start?: number; limit?: number; published?: boolean; featured?: boolean }) {
        const query = new URLSearchParams();
        if (params?.start !== undefined) query.append('start', params.start.toString());
        if (params?.limit !== undefined) query.append('limit', params.limit.toString());
        if (params?.published !== undefined) query.append('published', params.published.toString());
        if (params?.featured !== undefined) query.append('featured', params.featured.toString());

        return this.request<{
            data: Array<{
                id: string;
                slug: string;
                title: string;
                excerpt: string;
                content: string;
                read_time: string;
                featured: boolean;
                published: boolean;
                tags: string[];
                author_id: string;
                created_at: string;
                updated_at: string;
            }>;
            total: number;
            page: number;
            limit: number;
        }>(`/blog/admin/posts?${query.toString()}`);
    }

    async createBlogPost(data: {
        title: string;
        excerpt: string;
        content: string;
        read_time: string;
        tags: string[];
        featured?: boolean;
        published?: boolean;
    }) {
        return this.request<{
            id: string;
            slug: string;
            message: string;
        }>('/blog/posts', {
            method: 'POST',
            body: JSON.stringify(data),
        });
    }

    async updateBlogPost(id: string, data: {
        title?: string;
        excerpt?: string;
        content?: string;
        read_time?: string;
        tags?: string[];
        featured?: boolean;
        published?: boolean;
    }) {
        return this.request<{ message: string }>(`/blog/posts/${id}`, {
            method: 'PUT',
            body: JSON.stringify(data),
        });
    }

    async deleteBlogPost(id: string) {
        return this.request<{ message: string }>(`/blog/posts/${id}`, {
            method: 'DELETE',
        });
    }

    async cancelInvitation(workspaceId: string, invitationId: string) {
        return this.request<{
            success: boolean;
            message: string;
        }>(`/workspaces/${workspaceId}/invitations/${invitationId}`, {
            method: 'DELETE',
        });
    }

    // Activity Feed
    async getActivities(workspaceId: string, limit: number = 10) {
        return this.request<{
            activities: Array<{
                id: string;
                action: string;
                entity_type: string;
                entity_id: string;
                entity_title: string | null;
                metadata: string;
                created_at: string;
                user_name: string;
                user_avatar: string | null;
            }>;
        }>(`/workspaces/${workspaceId}/activities?limit=${limit}`);
    }

    // Team Analytics
    async getTeamAnalytics(workspaceId: string) {
        return this.request<{
            data: {
                team_progress: {
                    total_objectives: number;
                    average_progress: number;
                    on_track: number;
                    at_risk: number;
                    off_track: number;
                    completed: number;
                };
                member_contributions: Array<{
                    user_id: string;
                    full_name: string;
                    avatar_url: string | null;
                    tasks_completed_30d: number;
                    tasks_completed_7d: number;
                }>;
                workload_distribution: Array<{
                    user_id: string;
                    full_name: string;
                    avatar_url: string | null;
                    open_tasks: number;
                    overdue_tasks: number;
                    high_priority_tasks: number;
                }>;
                at_risk_items: Array<{
                    type: string;
                    id: string;
                    title: string;
                    progress: number;
                    status: string;
                    due_date: string | null;
                }>;
                velocity: Array<{
                    week: string;
                    tasks_completed: number;
                }>;
            };
        }>(`/analytics/team?workspace_id=${workspaceId}`);
    }

    // ============================================
    // AI CHAT
    // ============================================

    async sendChatMessage(workspaceId: string, message: string, model: 'llama' | 'deepseek' = 'llama') {
        return this.request<{
            response: string;
            model: string;
        }>('/chat', {
            method: 'POST',
            body: JSON.stringify({ workspaceId, message, model }),
        });
    }

    async getChatHistory(workspaceId: string, limit: number = 50) {
        return this.request<{
            messages: Array<{
                id: string;
                role: 'user' | 'assistant';
                content: string;
                model: string | null;
                created_at: string;
            }>;
        }>(`/chat/history?workspaceId=${workspaceId}&limit=${limit}`);
    }

    async clearChatHistory(workspaceId: string) {
        return this.request<{ success: boolean }>(`/chat/history?workspaceId=${workspaceId}`, {
            method: 'DELETE',
        });
    }

    // ============================================
    // GOOGLE CALENDAR INTEGRATION
    // ============================================

    async getCalendarStatus() {
        return this.request<{
            connected: boolean;
            syncEnabled: boolean;
            calendarId: string | null;
        }>('/calendar/status');
    }

    async getCalendarConnectUrl() {
        return this.request<{ authUrl: string }>('/calendar/connect');
    }

    async handleCalendarCallback(code: string) {
        return this.request<{ success: boolean; message: string }>('/calendar/callback', {
            method: 'POST',
            body: JSON.stringify({ code }),
        });
    }

    async disconnectCalendar() {
        return this.request<{ success: boolean; message: string }>('/calendar/disconnect', {
            method: 'DELETE',
        });
    }

    async syncTaskToCalendar(taskId: string) {
        return this.request<{ success: boolean; message: string; eventId?: string }>(`/calendar/sync-task/${taskId}`, {
            method: 'POST',
        });
    }

    async syncAllTasksToCalendar() {
        return this.request<{
            success: boolean;
            message: string;
            synced: number;
            failed: number;
            total: number;
        }>('/calendar/sync-all', {
            method: 'POST',
        });
    }

    async updateCalendarSettings(settings: { syncEnabled?: boolean; calendarId?: string }) {
        return this.request<{ success: boolean; message: string }>('/calendar/settings', {
            method: 'PATCH',
            body: JSON.stringify(settings),
        });
    }

    // ============================================
    // NOTES
    // ============================================

    async getNotes(workspaceId: string, options?: { parentId?: string | null; search?: string; archived?: boolean }) {
        const params = new URLSearchParams({ workspace_id: workspaceId });
        if (options?.parentId !== undefined) {
            params.append('parent_id', options.parentId || 'null');
        }
        if (options?.search) {
            params.append('search', options.search);
        }
        if (options?.archived) {
            params.append('archived', 'true');
        }
        return this.request<{
            data: Array<{
                id: string;
                workspace_id: string;
                title: string;
                content: string;
                icon: string | null;
                cover_url: string | null;
                is_pinned: number;
                is_archived: number;
                parent_id: string | null;
                position: number;
                created_at: string;
                updated_at: string;
                author_name: string;
                child_count: number;
            }>;
        }>(`/notes?${params.toString()}`);
    }

    async getNote(noteId: string) {
        return this.request<{
            data: {
                id: string;
                title: string;
                content: string;
                icon: string | null;
                cover_url: string | null;
                is_pinned: number;
                is_archived: number;
                parent_id: string | null;
                position: number;
                created_at: string;
                updated_at: string;
                author_name: string;
                children: Array<{ id: string; title: string; icon: string | null; is_pinned: number; position: number }>;
                breadcrumb: Array<{ id: string; title: string }>;
            };
        }>(`/notes/${noteId}`);
    }

    async createNote(data: {
        workspace_id: string;
        title?: string;
        content?: string;
        parent_id?: string | null;
        icon?: string | null;
    }) {
        return this.request<{ data: any }>('/notes', {
            method: 'POST',
            body: JSON.stringify(data),
        });
    }

    async updateNote(noteId: string, data: {
        title?: string;
        content?: string;
        icon?: string | null;
        cover_url?: string | null;
        parent_id?: string | null;
        position?: number;
    }) {
        return this.request<{ data: any }>(`/notes/${noteId}`, {
            method: 'PATCH',
            body: JSON.stringify(data),
        });
    }

    async toggleNotePin(noteId: string) {
        return this.request<{ success: boolean; is_pinned: boolean }>(`/notes/${noteId}/pin`, {
            method: 'PATCH',
        });
    }

    async toggleNoteArchive(noteId: string) {
        return this.request<{ success: boolean; is_archived: boolean }>(`/notes/${noteId}/archive`, {
            method: 'PATCH',
        });
    }

    async deleteNote(noteId: string) {
        return this.request<{ success: boolean; message: string }>(`/notes/${noteId}`, {
            method: 'DELETE',
        });
    }

    // Google Tasks Integration
    async getGoogleTasksStatus() {
        return this.request<{
            connected: boolean;
            oauth_url?: string;
            expires_at?: string;
            settings?: any;
            stats?: {
                total_synced_tasks: number;
                synced_lists: number;
                last_sync: string;
            };
        }>('/integrations/google-tasks/status');
    }

    async getGoogleTasksLists() {
        return this.request<{
            data: Array<{
                id: string;
                title: string;
                updated: string;
                synced: boolean;
                visionboard_board_id?: string;
                sync_direction?: string;
            }>;
        }>('/integrations/google-tasks/lists');
    }

    async connectGoogleTasks(code: string) {
        return this.request<{
            success: boolean;
            message: string;
        }>('/integrations/google-tasks/connect', {
            method: 'POST',
            body: JSON.stringify({ code }),
        });
    }

    async syncGoogleTasks(options?: { list_id?: string; force?: boolean }) {
        return this.request<{
            success: boolean;
            message: string;
            data: any;
        }>('/integrations/google-tasks/sync', {
            method: 'POST',
            body: JSON.stringify(options || {}),
        });
    }

    async disconnectGoogleTasks() {
        return this.request<{
            success: boolean;
            message: string;
        }>('/integrations/google-tasks/disconnect', {
            method: 'DELETE',
        });
    }

    async getSyncHistory(limit: number = 10) {
        return this.request<{
            data: Array<{
                id: string;
                sync_type: string;
                direction: string;
                tasks_synced: number;
                tasks_created: number;
                tasks_updated: number;
                tasks_deleted: number;
                errors_count: number;
                started_at: string;
                completed_at: string | null;
                duration_ms: number | null;
                status: string;
            }>;
        }>(`/integrations/google-tasks/sync-history?limit=${limit}`);
    }

    // Reports
    async getReports(workspaceId: string, period: 'week' | 'month' = 'week', date?: string) {
        const params = new URLSearchParams({ workspace_id: workspaceId, period });
        if (date) params.append('date', date);

        return this.request<{
            data: {
                period: string;
                period_start: string;
                period_end: string;
                summary: {
                    total_tasks: number;
                    completed: number;
                    completion_rate: number;
                    overdue: number;
                    by_priority: {
                        urgent: number;
                        high: number;
                        medium: number;
                        low: number;
                    };
                    change_vs_previous: {
                        completed_previous: number;
                        change_percent: number;
                        trend: 'up' | 'down' | 'same';
                    };
                };
                daily_breakdown: Array<{
                    date: string;
                    completed: number;
                    created: number;
                }>;
                by_key_result: Array<{
                    kr_id: string;
                    kr_title: string;
                    objective_title: string;
                    total_tasks: number;
                    completed_in_period: number;
                    overdue: number;
                }>;
                focus_recommendation: {
                    area: string;
                    objective: string;
                    reason: string;
                    kr_id: string;
                } | null;
            };
        }>(`/analytics/reports?${params.toString()}`);
    }

    // Board Google Tasks Sync
    async getBoardSyncStatus(boardId: string) {
        return this.request<{
            data: {
                enabled: boolean;
                task_list_id: string | null;
                task_list_name: string | null;
                last_sync_at: string | null;
                pending_operations: number;
            };
        }>(`/boards/${boardId}/sync/google-tasks/status`);
    }

    async enableBoardSync(boardId: string, data: {
        create_new_list?: boolean;
        task_list_id?: string;
        task_list_name?: string;
    }) {
        return this.request<{
            data: {
                id: string;
                board_id: string;
                task_list_id: string;
                task_list_name: string;
                enabled: boolean;
            };
        }>(`/boards/${boardId}/sync/google-tasks`, {
            method: 'POST',
            body: JSON.stringify(data)
        });
    }

    async disableBoardSync(boardId: string) {
        return this.request<{
            success: boolean;
            message: string;
        }>(`/boards/${boardId}/sync/google-tasks`, {
            method: 'DELETE'
        });
    }

    async getGoogleTaskLists() {
        return this.request<{
            data: Array<{
                id: string;
                title: string;
                updated: string;
            }>;
        }>('/google-tasks/lists');
    }
}

export const api = new ApiClient();
